import {
  AddIcon,
  Button,
  Checkbox,
  DeleteIcon,
  Grid,
  GridColumn,
  GridRow,
  Select,
  SelectOptionType,
} from '@bp/ui-components';
import { FieldArray, Form, Formik } from 'formik';
import { FormikHelpers } from 'formik/dist/types';
import { observer } from 'mobx-react-lite';
import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SingleValue } from 'react-select';
import { useMemorizedCacheTag } from '../../../hooks/useMemorizedCacheTag';
import {
  LessonFlag,
  useCreateTeachingBlockCardsMutation,
  useDeleteTeachingBlockCardsMutation,
  useLessonsForTeachingBlockVersionQuery,
  useSetLessonsFlagMutation,
} from '../../../types/planung-graphql-client-defs';
import { createIntArray } from '../../../utils/arrayFunc';
import { LessonCard } from '../../LessonCard/LessonCard';
import { ModalBottomButtons } from '../../ModalBottomButtons/ModalBottomButtons';
import { refreshTeachingBlockVersionQueries, useTeachingBlockVersionData } from '../hooks/useTeachingBlockVersionData';
import styles from './TeachingBlockCardsForm.module.scss';

type LessonsListFormProps = {
  versionUuid: string;
  subjectContainerUuid: string;
  onClose: () => void;
  lessonUuid: string;
};
/** Eine Karte im Formular. Nur ungesteckte, ungesperrte Karten lassen sich ändern oder entfernen. */
type FormCard = {
  uuid: string;
  duration?: number | null;
  locked: boolean;
  /** Gesetzt, wenn die Karte gesteckt ist. */
  startDate?: string | null;
  editable: boolean;
};

type LessonTeachingBlockCardFormType = {
  cards: FormCard[];
  lesson: {
    uuid: string;
    isClassTrip: boolean;
  };
};

export const TeachingBlockCardsForm: FC<LessonsListFormProps> = observer(
  ({ subjectContainerUuid, onClose, lessonUuid, versionUuid }) => {
    const { t } = useTranslation();
    const context = useMemorizedCacheTag('VERSIONS');

    const [, setLessonsFlag] = useSetLessonsFlagMutation();

    const [, createCards] = useCreateTeachingBlockCardsMutation();

    const [loading, setLoading] = useState(false);

    const [, deleteCards] = useDeleteTeachingBlockCardsMutation();
    const lessonContext = useMemorizedCacheTag('TEACHINGBLOCK_LESSONS');
    const lessonsContext = useMemorizedCacheTag('LESSONS');
    const teachingBlockCardContext = useMemorizedCacheTag('TEACHING_BLOCK_CARDS');

    // Karten aus dem Lesemodell der Version, die Zuordnung aus der Zuordnungs-Liste der Version –
    // beides ist auf den Seiten der Version schon geladen.
    const { versionData } = useTeachingBlockVersionData(versionUuid);
    const [{ data }] = useLessonsForTeachingBlockVersionQuery({
      variables: { versionUuid },
      context: lessonsContext,
      pause: !versionUuid,
    });

    const lesson = data?.lessonsForTeachingBlockVersion.find((l) => l.uuid === lessonUuid);

    // Vorgabe aus dem Deputat: je Einheit an dieser Schiene ihre Zahl an Karten.
    const originalCards: FormCard[] = lesson
      ? lesson.lessonUnits
          .filter((unit) => !!subjectContainerUuid && unit.subjectContainer?.uuid === subjectContainerUuid)
          .flatMap((unit) =>
            createIntArray(unit.count ?? 0).map(() => ({
              uuid: '',
              duration: unit.duration,
              locked: false,
              editable: true,
            })),
          )
      : [];

    if (lesson) {
      // ungesteckte Karten zuerst
      const _cards = [...(versionData?.cards ?? [])]
        .filter((card) => card.lessonUuid === lessonUuid)
        .sort((card, card2) => {
          if (card.startDate && !card2.startDate) {
            return 1;
          } else if (!card.startDate && card2.startDate) {
            return -1;
          } else {
            return 0;
          }
        });

      const initialValue: LessonTeachingBlockCardFormType = {
        lesson: {
          uuid: lesson.uuid,
          isClassTrip: lesson.isClassTrip ?? false,
        },
        cards: _cards.map((card) => ({
          uuid: card.uuid,
          duration: card.duration,
          locked: card.locked,
          startDate: card.startDate,
          editable: !card.locked && !card.startDate,
        })),
      };

      const handleSubmit = async (
        values: LessonTeachingBlockCardFormType,
        formikHelpers: FormikHelpers<LessonTeachingBlockCardFormType>,
      ) => {
        if (versionUuid) {
          setLoading(true);
          const newCards = values.cards.filter((card) => card.editable);
          const cardUuidsToRemoveUuids = initialValue.cards.filter((card) => card.editable).map((card) => card.uuid);

          if (cardUuidsToRemoveUuids.length > 0) {
            await deleteCards({ cardUuids: cardUuidsToRemoveUuids }, teachingBlockCardContext);
          }

          if (newCards.length > 0) {
            await createCards(
              {
                versionUuid,
                lessonUuid: lesson.uuid,
                durations: newCards.map((newCard) => Number(newCard.duration)),
              },
              context,
            );
          }

          await setLessonsFlag(
            { uuids: [values.lesson.uuid], flag: LessonFlag.IsClassTrip, value: values.lesson.isClassTrip },
            lessonContext,
          );
          // Löschen, Anlegen und Kennzeichen laufen nacheinander; danach einmal frisch laden.
          await refreshTeachingBlockVersionQueries(versionUuid);

          onClose();
          setLoading(false);
          formikHelpers.resetForm();
        }
      };
      const subjectContainerOptions: SelectOptionType[] = createIntArray(52).map((value) => {
        return {
          label: `${t('common.weeksCount', { count: value + 1 })}`,
          value: value + 1,
        };
      });

      let originalSumDuration = 0;
      originalCards.forEach((card) => {
        if (card.duration) {
          originalSumDuration = originalSumDuration + card.duration;
        }
      });

      const defaultCard: FormCard = { duration: 1, uuid: '', editable: true, locked: false };

      return (
        <>
          <Formik initialValues={initialValue} onSubmit={handleSubmit} enableReinitialize={true}>
            {({ setFieldValue, values, isSubmitting, dirty, resetForm }) => {
              const placedCards = values.cards.filter((card) => {
                return !card.editable;
              });
              const cardsForBulkEdit = values.cards.filter((card) => card.editable);

              const setCards = async (_cards: FormCard[]) => {
                await setFieldValue('cards', [..._cards, ...placedCards]);
              };
              const cardsForBulkEditDuration = cardsForBulkEdit.reduce((sum, card) => sum + (card.duration || 1), 0);

              return (
                <Form>
                  <div className={styles['lessons-list-form']}>
                    <Grid className={'mb-8'}>
                      <GridRow spacingBottom={'s'} spacingTop={'none'}>
                        <GridColumn width={2}>{`${t('subject.title', { count: 1 })}: `}</GridColumn>
                        <GridColumn width={2}>{lesson?.subject.name}</GridColumn>
                      </GridRow>
                      <GridRow spacingBottom={'s'} spacingTop={'none'}>
                        <GridColumn width={2}>{`${t('persons.title', { count: 1 })}: `}</GridColumn>
                        <GridColumn width={2}>
                          {`${lesson.teachers.map((teacher) => teacher.displayNameShort).join(', ')}`}
                        </GridColumn>
                      </GridRow>
                      <GridRow spacingBottom={'s'} spacingTop={'none'}>
                        <GridColumn width={2}>{`${t('classes.title', { count: 2 })}:`}</GridColumn>
                        <GridColumn
                          width={2}
                        >{` ${lesson.lessonClasses.map((lessonClass) => lessonClass.class.shortName).join(', ')}`}</GridColumn>
                      </GridRow>
                      {!lesson.onlyInTeachingBlockVersion && (
                        <GridRow spacingBottom={'s'} spacingTop={'m'}>
                          <GridColumn width={2}>{`${t('lesson.plannedWeeks')}:`}</GridColumn>
                          <GridColumn width={2}>{`${originalSumDuration}`}</GridColumn>
                        </GridRow>
                      )}
                      <GridRow spacingBottom={'s'} spacingTop={lesson.onlyInTeachingBlockVersion ? 'm' : 'none'}>
                        <GridColumn width={2}>{`${t('lesson.givenWeeks')}:`}</GridColumn>
                        <GridColumn width={2}>{`${values.cards.reduce(
                          (totalDuration, card) => totalDuration + (card.duration ?? 0),
                          0,
                        )}`}</GridColumn>
                      </GridRow>
                    </Grid>
                    <div className={styles['form-items']}>
                      <Grid>
                        <GridRow className={'tks__row'}>
                          <GridColumn width={5}>
                            {subjectContainerUuid && (
                              <Button
                                hierarchy={'secondary'}
                                disabled={cardsForBulkEdit.length <= 1}
                                onClick={async () => {
                                  await setCards([{ ...defaultCard, duration: cardsForBulkEditDuration }]);
                                }}
                              >
                                {t('subjectContainer.allConnected')}
                              </Button>
                            )}
                            {!lesson?.onlyInTeachingBlockVersion && (
                              <Button
                                hierarchy={'secondary'}
                                disabled={placedCards.length > 0}
                                onClick={async () => {
                                  await setFieldValue('cards', originalCards);
                                }}
                              >
                                {t('lesson.reset')}
                              </Button>
                            )}
                          </GridColumn>

                          <GridColumn width={3}>
                            <Checkbox
                              className={'mt-2'}
                              label={t('lesson.basics.isClassTrip')}
                              name={'lesson.isClassTrip'}
                              checked={values.lesson.isClassTrip}
                              onChange={async () => {
                                await setFieldValue('lesson', {
                                  ...values.lesson,
                                  isClassTrip: !values.lesson.isClassTrip,
                                });
                              }}
                            />
                          </GridColumn>
                        </GridRow>
                      </Grid>
                      <div className={`${styles['items-wrapper']} tks__grid no-gap`}>
                        <FieldArray name={'cards'}>
                          {(arrayHelpers) => {
                            return (
                              <>
                                {values.cards.map((card, index) => {
                                  return (
                                    <div className='tks__row big-gap' key={index}>
                                      <div className='tks__col col-xs-5'>
                                        <Select
                                          options={subjectContainerOptions}
                                          disabled={card.locked || !card.editable}
                                          value={subjectContainerOptions.find((o) => o.value === card.duration)}
                                          onChange={async (option) => {
                                            const opt = option as SingleValue<SelectOptionType>;
                                            await setFieldValue(`cards.${index}`, { ...card, duration: opt?.value });
                                          }}
                                          name={`cards.${index}`}
                                          label={`${t('lesson.weekCard')} ${index + 1}`}
                                        ></Select>
                                      </div>
                                      <div className='tks__col col-xs-2 align-center mt-2'>
                                        <LessonCard
                                          lessonClasses={lesson.lessonClasses[0]}
                                          teacherColors={lesson.teachers.map((teacher) => teacher.color ?? '')}
                                          label={lesson.subject.shortName}
                                          count={1}
                                          duration={card.duration}
                                        />
                                      </div>
                                      <div className='tks__col col-xs-5 align-center mt-2'>
                                        <div className={'tks__row justify-end'}>
                                          {card.locked ? (
                                            <div className={'align-center'}>{t('card.locked')}</div>
                                          ) : card.startDate ? (
                                            <div className={'align-center'}>{t('card.placed')}</div>
                                          ) : (
                                            <></>
                                          )}
                                          <Button
                                            hierarchy='tertiary'
                                            disabled={!card.editable}
                                            onClick={async () => {
                                              await arrayHelpers.remove(index);
                                            }}
                                            icon={<DeleteIcon />}
                                          />
                                        </div>
                                      </div>
                                    </div>
                                  );
                                })}
                                <Button
                                  hierarchy={'tertiary'}
                                  icon={<AddIcon />}
                                  onClick={async () => {
                                    await setFieldValue('cards', [...cardsForBulkEdit, defaultCard, ...placedCards]);
                                  }}
                                >
                                  {t('lesson.weekCard')}
                                </Button>
                              </>
                            );
                          }}
                        </FieldArray>
                      </div>
                    </div>

                    <ModalBottomButtons
                      closeButton={{
                        callback: () => {
                          resetForm();
                          onClose();
                        },
                        text: t('common.cancelChanges'),
                      }}
                      submitButton={{
                        disabled: isSubmitting || !dirty,
                      }}
                      isLoading={loading}
                    />
                  </div>
                </Form>
              );
            }}
          </Formik>
        </>
      );
    }
    return <></>;
  },
);
